/** Audience and delivery toggles in the Admin notification matrix. */
export type NotificationMatrixToggle = 'user' | 'fan' | 'email' | 'push' | 'alert'

export type NotificationTypeKey =
  | 'forgotToClockOut'
  | 'pto'
  | 'controlCenterReportDone'
  | 'inventory'
  | 'assets'
  | 'certifications'
  | 'municipalityLicenses'

export type NotificationEventDef = {
  key: string
  label: string
}

export type NotificationTypeDef = {
  key: NotificationTypeKey
  label: string
  /** When present, Admin matrix shows each event as its own fully configurable sub-row. */
  events?: NotificationEventDef[]
  /**
   * False = shown crossed-out in Admin (not yet emitted by backend).
   * Defaults to true when omitted.
   */
  wired?: boolean
  /**
   * True = org-level type with no subject; Admin hides User and forces Fan.
   */
  fanOnly?: boolean
}

/** Configurable notification types for Admin access-level matrix. */
export const NOTIFICATION_TYPES: NotificationTypeDef[] = [
  { key: 'forgotToClockOut', label: 'Forgot to clock out' },
  {
    key: 'pto',
    label: 'PTO',
    wired: false,
    events: [
      { key: 'made', label: 'Request made' },
      { key: 'accepted', label: 'Request accepted' },
    ],
  },
  { key: 'controlCenterReportDone', label: 'Control Center Report Done', wired: false },
  {
    key: 'inventory',
    label: 'Inventory',
    fanOnly: true,
    events: [
      { key: 'new', label: 'New' },
      { key: 'updated', label: 'Updated' },
      { key: 'checkedOut', label: 'Checked out to job' },
      { key: 'deleted', label: 'Deleted' },
    ],
  },
  {
    key: 'assets',
    label: 'Assets',
    fanOnly: true,
    events: [
      { key: 'new', label: 'New' },
      { key: 'updated', label: 'Updated' },
      { key: 'deleted', label: 'Deleted' },
    ],
  },
  {
    key: 'certifications',
    label: 'Certifications',
    events: [
      { key: 'created', label: 'Created' },
      { key: 'updated', label: 'Updated' },
      { key: 'expiring', label: 'Expiring (90/60/30)' },
    ],
  },
  {
    key: 'municipalityLicenses',
    label: 'Municipal licenses',
    fanOnly: true,
    events: [
      { key: 'created', label: 'Created' },
      { key: 'updated', label: 'Updated' },
      { key: 'deleted', label: 'Deleted' },
      { key: 'expiring', label: 'Expiring (90/60/30)' },
    ],
  },
]

export function isNotificationTypeWired(key: string): boolean {
  const def = getNotificationTypeDef(key)
  if (!def) return false
  return def.wired !== false
}

export function isNotificationTypeFanOnly(key: string): boolean {
  return !!getNotificationTypeDef(key)?.fanOnly
}

export function getNotificationTypeDef(key: string): NotificationTypeDef | undefined {
  return NOTIFICATION_TYPES.find((t) => t.key === key)
}

export function eventKeysForType(key: string): string[] {
  return getNotificationTypeDef(key)?.events?.map((e) => e.key) || []
}
